import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import CityCardGame from "./components/CityCardGame";
import AuthCallback from "./components/AuthCallback";
import NicknameSetup from "./components/NicknameSetup";
import Leaderboard from "./components/Leaderboard";
import AdminDashboard from "./components/AdminDashboard";
import StatsPage from "./components/StatsPage";
import OfflineBanner from "./components/OfflineBanner";
import { Toaster } from "./components/ui/toaster";
import { setupConnectivityListener, getPendingScores, isTauri } from "./utils/tauri";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Separate component to detect session_id in URL hash
function AuthDetector() {
  const location = useLocation();
  
  // Check URL fragment for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Check for /admin route
  if (location.pathname === '/admin') {
    return <AdminRoute />;
  }
  
  // Check for /stats route
  if (location.pathname === '/stats') {
    return <StatsRoute />;
  }
  
  return <MainApp />;
}

// Admin Route Component
function AdminRoute() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const sessionToken = localStorage.getItem('session_token');
        const headers = {};
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`;
        }
        
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include',
          headers
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <AdminDashboard 
      user={user} 
      onBack={() => navigate('/')} 
    />
  );
}

// Stats Route Component
function StatsRoute() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const sessionToken = localStorage.getItem('session_token');
        const headers = {};
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`;
        }
        
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include',
          headers
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          // Not logged in, redirect to home
          navigate('/');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <StatsPage 
      user={user} 
      onBack={() => navigate('/')} 
    />
  );
}

function MainApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsNickname, setNeedsNickname] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [challengeData, setChallengeData] = useState(null);

  // Check for challenge parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const challengeId = params.get('challenge');
    
    if (challengeId) {
      // Fetch challenge data
      const fetchChallenge = async () => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${backendUrl}/api/challenges/${challengeId}`);
          if (response.ok) {
            const data = await response.json();
            setChallengeData(data);
          }
        } catch (error) {
          console.error('Failed to fetch challenge:', error);
        }
      };
      fetchChallenge();
      
      // Clean up URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check if user passed from AuthCallback
  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setNeedsNickname(location.state.needsNickname || !location.state.user.nickname);
      setIsLoading(false);
      // Clear the state to prevent issues on refresh
      window.history.replaceState({}, document.title);
      return;
    }

    // Check existing session
    const checkAuth = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const sessionToken = localStorage.getItem('session_token');
        
        const headers = {};
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`;
        }
        
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include',
          headers,
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setNeedsNickname(!userData.nickname);
        } else {
          // Clear invalid token
          localStorage.removeItem('session_token');
        }
      } catch (error) {
        console.log('Not authenticated');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.state]);

  const handleLogout = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const sessionToken = localStorage.getItem('session_token');
      
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear localStorage token
    localStorage.removeItem('session_token');
    setUser(null);
    setNeedsNickname(false);
  };

  const handleNicknameComplete = (updatedUser) => {
    setUser(updatedUser);
    setNeedsNickname(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Show nickname setup if user is logged in but hasn't set nickname
  if (user && needsNickname) {
    return <NicknameSetup user={user} onComplete={handleNicknameComplete} />;
  }

  // Show leaderboard
  if (showLeaderboard) {
    return (
      <Leaderboard
        user={user}
        onBack={() => setShowLeaderboard(false)}
      />
    );
  }

  // Show stats page
  if (showStats) {
    return (
      <StatsPage
        user={user}
        onBack={() => setShowStats(false)}
      />
    );
  }

  return (
    <CityCardGame
      user={user}
      onLogout={handleLogout}
      onViewLeaderboard={() => setShowLeaderboard(true)}
      onViewStats={() => setShowStats(true)}
      onNicknameChange={handleNicknameComplete}
      challengeData={challengeData}
      onChallengeComplete={() => setChallengeData(null)}
    />
  );
}

function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingScores, setPendingScores] = useState(0);

  useEffect(() => {
    // Set up connectivity listener
    const cleanup = setupConnectivityListener(
      () => {
        setIsOffline(false);
        // Refresh pending count when back online
        setPendingScores(getPendingScores().length);
      },
      () => setIsOffline(true)
    );

    // Check pending scores on mount
    setPendingScores(getPendingScores().length);

    // Log platform info for debugging
    if (isTauri()) {
      console.log('Running in Tauri desktop app');
    }

    return cleanup;
  }, []);

  return (
    <div className="App">
      {isOffline && <OfflineBanner pendingScores={pendingScores} />}
      <BrowserRouter>
        <AuthDetector />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
